<script lang="ts" setup>
import type {
  Filter,
  HandHistoryReportColumn,
  AnalyzerReport,
  ActiveAnalyzerReport,
  FilterCategory,
} from '@/types/api/reports'
import { computed, toRef } from 'vue'
import ReportTable from '@/components/hhv3/Analyzer/ReportTable/ReportTable.vue'
import {
  getDragExpectedProps,
  type DragDropArgs,
} from '@/components/lib/GwDragDropper/GwDragDropperUtils'
import FilterBar from '@/components/hhv3/Analyzer/FilterBar.vue'
import StatsBoxes from '@/components/hhv3/Analyzer/StatsBoxes/StatsBoxes.vue'
import { type TReportTableColumn } from '@/components/lib/GwTable/gwTableUtils'
import type { SelectFilterParams } from '@/components/hhv3/Analyzer/StatsTable/SimpleTable.vue'
import type { ApiError } from '../../../types/basic'

export interface AnalyzerReportProps {
  report: ActiveAnalyzerReport
  filterCategories: FilterCategory[]
  loading: boolean
}

const props = defineProps<AnalyzerReportProps>()
const emit = defineEmits<{
  'click:view': [event: MouseEvent, report: AnalyzerReport['selected_view']]
  'update:columns': [columns: HandHistoryReportColumn[]]
  'update:filters': [filters: Filter[]]
  'new:filters': [filters: Filter[]]
  setError: [error: ApiError]
}>()

const activeTableColumns = toRef(() => props.report.saved_columns || [])

const handleThClick = async (_e: Event, item: TReportTableColumn) => {
  const col = props.report.saved_columns.find(({ column }) => column.field_name === item.field)
  if (col?.column.is_ordering_available) {
    const activeColumn = props.report.saved_columns.find(column => column.column.id === item.id)
    const newValue = activeColumn?.sort_direction === 'ASC' ? 'DESC' : 'ASC'

    const newColumns = props.report.saved_columns.map(column => ({
      ...column,
      sort_direction: column.column.field_name === item.field ? newValue : null,
    })) as HandHistoryReportColumn[]

    emit('update:columns', newColumns)
  }
}

const handleThDrop = (_e: Event, dragArgs: DragDropArgs<HandHistoryReportColumn>) => {
  const thItem = dragArgs.draggedData
  const draggedItems = [thItem]

  const {
    did_drop_on_dragged, //
    expected_items,
  } = getDragExpectedProps({
    items: activeTableColumns.value,
    draggedItems,
    dragArgs,
    orderingKey: undefined,
    getId: item => item.column.id.toString(),
  })

  if (did_drop_on_dragged) return

  emit('update:columns', expected_items || [])
}

const toggleColumn = (item: HandHistoryReportColumn) => {
  const updatedColumns = props.report.saved_columns.map(col => {
    if (col.column.id === item.column.id && col.is_hidden !== undefined) {
      return { ...col, is_hidden: !item.is_hidden }
    }

    return col
  })

  emit('update:columns', updatedColumns)
}

const removeFilterField = (item: Filter) => {
  const udpatedItemPart: Partial<Filter> = {
    is_active: false,
    value: null,
  }

  const updatedFilters = props.report.saved_filters.map(filter => {
    if (filter.filter.id === item.filter.id) {
      return {
        ...filter,
        ...udpatedItemPart,
      }
    }

    return filter
  })

  emit('update:filters', updatedFilters)
}

const updateFilterField = (item: Filter) => {
  if (item.value === null || (typeof item.value === 'object' && !item.value.length)) {
    removeFilterField(item)
    return
  }

  const updatedFilters = props.report.saved_filters.map(filter => {
    if (filter.filter.id === item.filter.id) {
      return item
    }
    return filter
  })
  emit('update:filters', updatedFilters)
}
const handleSwitchView = (e: MouseEvent, view: AnalyzerReport['selected_view']) => {
  emit('click:view', e, view)
}

const selectedView = computed(() => props.report.selected_view || 'TABLE')
const filters = computed(() => props.report.saved_filters || [])

const handleCellClick = ({ row, cell, gqlParam }: SelectFilterParams) => {
  const updatedFilters: Filter[] = []

  const row_filter = filters.value.find(
    filter =>
      filter.filter.field_name === gqlParam && filter.filter?.available_choices?.includes(row),
  )
  if (row_filter)
    updatedFilters.push({
      ...row_filter,
      value: row_filter?.filter?.api_filters[0]?.filter_type === 'MULTIPLE_CHOICE' ? [row] : row,
      is_active: true,
    })

  const cell_filter = filters.value.find(filter => filter.filter.field_name === 'hand_correctness')
  if (cell_filter)
    updatedFilters.push({
      ...cell_filter,
      value: [cell],
      is_active: true,
    })
  emit('new:filters', [
    ...filters.value.filter(filter => updatedFilters.every(f => f.filter.id !== filter.filter.id)),
    ...updatedFilters,
  ])
  emit('click:view', new MouseEvent('click'), 'TABLE')
}
</script>
<template>
  <div
    class="anreport fnt_main f-column"
    :class="loading && 'gw_loading gw_loading_text'"
  >
    <FilterBar
      :selectedView
      :columns="activeTableColumns"
      :filters
      :filterCategories
      tstPrefix="report"
      @toggle:column="toggleColumn"
      @drag:drop:column="handleThDrop"
      @update:filter="updateFilterField"
      @remove:filter="removeFilterField"
      @click:view="handleSwitchView"
    />
    <ReportTable
      v-if="selectedView === 'TABLE'"
      :columns="activeTableColumns"
      :filters
      :reportId="report.id || ''"
      @th:click="handleThClick"
      @drag:drop:column="handleThDrop"
    />

    <StatsBoxes
      v-else-if="selectedView === 'STATS'"
      :filters
      @cell:click="handleCellClick"
    />
  </div>
</template>
<style scoped lang="scss">
.anreport {
  flex: 1;
  min-width: 0;
  min-height: 0;
}
</style>
